/* ==========================================================================
 *  Cross-portal navigation
 *
 *  The five portals are separate origins in production (riskpay.in,
 *  merchant.riskpay.in, …) and five localhost ports in development. Every link
 *  between them therefore has to be resolved, not hard-coded — a "Get started"
 *  button that sends a developer testing on localhost off to the live
 *  production signup page is worse than a broken link, because it looks like
 *  it worked.
 *
 *  Resolution order, per portal:
 *    1. An explicit VITE_*_URL, so a staging deploy can point anywhere.
 *    2. The production subdomain, in a production build.
 *    3. The conventional localhost port, in development.
 *
 *  NOTE: this covers navigation only. URLs inside the documentation's code
 *  samples deliberately stay as api.riskpay.in — a reader copying a cURL
 *  example wants the real endpoint, not whatever host they happen to be
 *  browsing from.
 * ========================================================================== */

export type PortalName = 'landing' | 'merchant' | 'employees' | 'quantum' | 'docs';

const PRODUCTION: Record<PortalName, string> = {
  landing: 'https://riskpay.in',
  merchant: 'https://merchant.riskpay.in',
  employees: 'https://employees.riskpay.in',
  quantum: 'https://quantum.riskpay.in',
  docs: 'https://docs.riskpay.in',
};

const LOCAL: Record<PortalName, string> = {
  landing: 'http://localhost:5173',
  merchant: 'http://localhost:5174',
  employees: 'http://localhost:5175',
  quantum: 'http://localhost:5176',
  docs: 'http://localhost:5177',
};

const ENV_KEY: Record<PortalName, string> = {
  landing: 'VITE_LANDING_URL',
  merchant: 'VITE_MERCHANT_URL',
  employees: 'VITE_EMPLOYEES_URL',
  quantum: 'VITE_QUANTUM_URL',
  docs: 'VITE_DOCS_URL',
};

/**
 * Vite replaces `import.meta.env` at build time. Read through a guarded helper
 * so this module stays importable from a plain Node context (tests, SSR) where
 * `import.meta.env` is undefined.
 */
function viteEnv(): Record<string, string | boolean | undefined> {
  try {
    return (import.meta as unknown as { env?: Record<string, string | boolean | undefined> }).env ?? {};
  } catch {
    return {};
  }
}

function resolve(portal: PortalName): string {
  const env = viteEnv();
  const override = env[ENV_KEY[portal]];
  if (typeof override === 'string' && override.length > 0) return override.replace(/\/$/, '');

  // Vite sets PROD on `vite build` and DEV on `vite dev`.
  const isProduction = env.PROD === true;
  return (isProduction ? PRODUCTION : LOCAL)[portal];
}

/**
 * Origin of the API.
 *
 * Empty in development, where every app proxies `/api` to localhost:4000 and a
 * relative path is exactly right. In production the portals are on their own
 * subdomains and the API is on another, so a relative `/api/v1/...` would
 * resolve against `merchant.riskpay.in` and 404 — set `VITE_API_ORIGIN` at
 * build time and every request lands on the API instead.
 *
 * Read at build time, not runtime: Vite inlines `import.meta.env`, so this is
 * baked into the bundle and changing it means rebuilding.
 */
export function apiOrigin(): string {
  const value = viteEnv().VITE_API_ORIGIN;
  return typeof value === 'string' && value.length > 0 ? value.replace(/\/+$/, '') : '';
}

/** Absolute API URL for a path that starts with `/api/...`. */
export function apiUrl(path: string): string {
  return `${apiOrigin()}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Base URL of each portal, resolved once at module load. */
export const portals: Record<PortalName, string> = {
  landing: resolve('landing'),
  merchant: resolve('merchant'),
  employees: resolve('employees'),
  quantum: resolve('quantum'),
  docs: resolve('docs'),
};

/** Builds a link into a portal. `portalUrl('merchant', '/signup')`. */
export function portalUrl(portal: PortalName, path = ''): string {
  if (!path) return portals[portal];
  return `${portals[portal]}${path.startsWith('/') ? path : `/${path}`}`;
}

/* Shorthands for the links that appear most often. */
export const signupUrl = () => portalUrl('merchant', '/signup');
export const loginUrl = () => portalUrl('merchant', '/login');
export const docsUrl = (path = '') => portalUrl('docs', path);
